/**
 * NPC Spawn Manager
 * 
 * Manages NPC spawn markers placed in maps. Handles:
 * - Registering spawn points from LevelBuilder
 * - Proximity-based spawning when player gets close
 * - NPC lifecycle management (spawn/despawn)
 * - AI behavior assignment via composable behavior system
 * 
 * Spawn radius of 0 means NPC is always spawned (for important NPCs).
 */

import { Scene, Vector3 } from '@babylonjs/core';
import { SplineConstraint } from './SplineConstraint';
import { NPCController } from './NPCController';
import { SplinePatrolAI, SplinePatrolConfig } from '../ai/SplinePatrolAI';
import { resolveBehavior, BehaviorAdapter, type NPCBehaviorConfig } from '../ai';
import { getCharacterConfig } from '../behaviors';
import { PhysicsManager } from './physics';
import { EntityRegistry } from '../core/EntityRegistry';
import { EventBus } from '../core/EventBus';
import { EntityType, CharacterSubtype } from '../types/EntityTypes';
import { CharacterAssetCache } from './CharacterAssetCache';
import { CombatSystem } from './CombatSystem';
import { logger } from '../core/Logger';
import type { CombatDeathEvent } from '../types/combat';

/**
 * Data for an NPC spawn point extracted from map
 */
export interface NPCSpawnData {
  /** Unique spawn ID (from entity marker) */
  id: string;
  
  /** Character config ID (e.g., 'hotdog', 'robo') */
  characterId: string;
  
  /** Spawn position from marker */
  position: Vector3;
  
  /** Spawn T value on spline (calculated from position) */
  spawnT: number;
  
  /** Radius at which player triggers spawn (0 = always spawned) */
  spawnRadius: number;
  
  /** Patrol left distance (meters) */
  patrolLeftDistance: number;
  
  /** Patrol right distance (meters) */
  patrolRightDistance: number;
  
  /** Patrol wait time at endpoints (seconds) */
  patrolWaitTime: number;
  
  /** Override max health (if not set, uses character config default) */
  maxHealth?: number;
  
  /** Override starting health (if not set, defaults to maxHealth) */
  startHealth?: number;

  /** Whether NPC state persists across map changes (default true) */
  persistState?: boolean;
  
  /** Whether to despawn on death (default true) */
  despawnOnDeath?: boolean;
  
  /** Behavior configuration from map annotations */
  behaviorConfig?: NPCBehaviorConfig;
}

/**
 * Runtime NPC instance data
 */
interface NPCInstance {
  spawnData: NPCSpawnData;
  controller: NPCController | null;
  behaviorAdapter: BehaviorAdapter | null;
  isSpawned: boolean;
  isDead: boolean;
}

/**
 * Manager for NPC spawn markers and proximity spawning
 */
export class NPCSpawnManager {
  private scene: Scene;
  private splineConstraint: SplineConstraint;
  private physicsManager: PhysicsManager;
  private entityRegistry: EntityRegistry;
  private eventBus?: EventBus;
  private characterAssetCache?: CharacterAssetCache;
  private combatSystem?: CombatSystem;
  
  private spawns: Map<string, NPCInstance> = new Map();
  private activeNPCs: NPCController[] = [];
  
  /** Enable immediate spawning for NPCs with spawnRadius=0 */
  private autoSpawnEnabled: boolean = true;
  
  constructor(
    scene: Scene,
    splineConstraint: SplineConstraint,
    physicsManager: PhysicsManager,
    entityRegistry: EntityRegistry,
    characterAssetCache?: CharacterAssetCache,
    eventBus?: EventBus,
    combatSystem?: CombatSystem
  ) {
    this.scene = scene;
    this.splineConstraint = splineConstraint;
    this.physicsManager = physicsManager;
    this.entityRegistry = entityRegistry;
    this.characterAssetCache = characterAssetCache;
    this.eventBus = eventBus;
    this.combatSystem = combatSystem;
    
    // Subscribe to death events to trigger behavior onDeath hooks
    if (this.eventBus) {
      this.eventBus.on('combat:death', (e) => this.onCharacterDeath(e as CombatDeathEvent));
    }
    
    logger.npc.info('NPCSpawnManager initialized');
  }
  
  /**
   * Handle character death events
   */
  private onCharacterDeath(event: CombatDeathEvent): void {
    const instance = this.spawns.get(event.characterId);
    if (!instance || !instance.controller || !instance.behaviorAdapter) {
      return;
    }
    
    // Disable collision immediately so dead NPC doesn't block player
    instance.controller.disableCollision();
    
    // Trigger behavior's onDeath hook
    instance.behaviorAdapter.onDeath(instance.controller, event.killerId ?? 'unknown');
    
    // Mark as dead
    this.markDead(event.characterId);
  }
  
  /**
   * Register a spawn point from level data
   */
  registerSpawn(data: NPCSpawnData): void {
    // Calculate T value from position if not provided
    if (data.spawnT === undefined || data.spawnT === -1) {
      const result = this.splineConstraint.project(data.position, 0);
      data.spawnT = result.projection.t;
      const splinePoint = result.projection.splinePoint;
      const distFromSpline = data.position.subtract(splinePoint).length();
      logger.npc.warn(`🔍 NPC projection: marker=${data.position.toString()} → splinePoint=${splinePoint.toString()} (distance=${distFromSpline.toFixed(2)}m, T=${data.spawnT.toFixed(3)})`);
      // Also verify getPointAt returns the same position
      const verifyPoint = this.splineConstraint.getPointAt(data.spawnT);
      logger.npc.warn(`🔍 Verification: getPointAt(${data.spawnT.toFixed(3)}) = ${verifyPoint.toString()}`);
    }
    
    const instance: NPCInstance = {
      spawnData: data,
      controller: null,
      behaviorAdapter: null,
      isSpawned: false,
      isDead: false,
    };
    
    this.spawns.set(data.id, instance);
    
    logger.npc.info(`📍 Registered NPC spawn: ${data.id} (${data.characterId}) at T=${data.spawnT.toFixed(3)}, radius=${data.spawnRadius}m`);
    
    // Immediately spawn if radius is 0 (always visible)
    if (data.spawnRadius === 0 && this.autoSpawnEnabled) {
      this.spawnNPC(data.id).catch(err => {
        logger.npc.error(`Failed to auto-spawn ${data.id}:`, err);
      });
    }
  }
  
  /**
   * Update proximity checks and NPC behaviors
   * @param playerPosition Current player position
   * @param playerT Player's T position on spline
   * @param deltaTime Frame delta time
   */
  update(playerPosition: Vector3, playerT: number, deltaTime: number): void {
    // Get spline length to convert meters to T-distance
    const splineLength = this.splineConstraint.getTotalLength();
    
    // Check proximity for spawning/despawning
    for (const [id, instance] of this.spawns) {
      // Dead NPCs should never respawn (despawnOnDeath defaults to true)
      if (instance.isDead && instance.spawnData.despawnOnDeath !== false) {
        continue;
      }
      
      const spawnRadius = instance.spawnData.spawnRadius || 20;
      
      // Convert spawn radius (meters) to T-distance on spline
      const spawnRadiusT = spawnRadius / splineLength;
      
      // Calculate T-distance between player and NPC spawn point
      const tDistance = Math.abs(playerT - instance.spawnData.spawnT);
      
      // Update player distance and T on behavior adapter for detection
      if (instance.behaviorAdapter && instance.controller) {
        const npcPos = instance.controller.character.position;
        const npcDistanceToPlayer = Vector3.Distance(playerPosition, npcPos);
        instance.behaviorAdapter.setPlayerDistance(npcDistanceToPlayer);
        instance.behaviorAdapter.setPlayerT(playerT);
      }
      
      if (spawnRadius > 0) {
        // Proximity-based spawning using spline T-distance
        if (!instance.isSpawned && tDistance <= spawnRadiusT) {
          this.spawnNPC(id).catch(err => {
            logger.npc.error(`Failed to spawn ${id}:`, err);
          });
        } else if (instance.isSpawned && tDistance > spawnRadiusT * 1.5) {
          // Despawn when player moves away (with hysteresis)
          this.despawnNPC(id);
        }
      }
    }
    
    // Update all active NPCs
    for (const npc of this.activeNPCs) {
      npc.update(deltaTime);
      
      // Check if NPC is ready to despawn (death animation complete)
      if (npc.consumeDespawnRequest()) {
        const spawnId = npc.getId();
        logger.npc.info(`💀 NPC ${spawnId} death animation complete, despawning`);
        this.despawnNPC(spawnId);
      }
    }
  }
  
  /**
   * Spawn an NPC instance
   */
  async spawnNPC(spawnId: string): Promise<NPCController | null> {
    const instance = this.spawns.get(spawnId);
    if (!instance) {
      logger.npc.warn(`Cannot spawn: unknown spawn ID ${spawnId}`);
      return null;
    }
    
    if (instance.isSpawned && instance.controller) {
      logger.npc.debug(`NPC ${spawnId} already spawned`);
      return instance.controller;
    }
    
    const data = instance.spawnData;
    
    // Get character config
    const charConfig = getCharacterConfig(data.characterId);
    if (!charConfig) {
      logger.npc.error(`Unknown character ID: ${data.characterId}`);
      return null;
    }
    
    // Create NPC controller
    const npc = new NPCController(
      this.scene,
      this.splineConstraint,
      charConfig,
      data.id,
      this.physicsManager
    );
    
    // Wire combat system for NPC attacks
    if (this.combatSystem) {
      npc.setCombatSystem(this.combatSystem);
    }
    
    // Load character assets
    if (this.characterAssetCache) {
      try {
        const cachedAsset = this.characterAssetCache.get(data.characterId);
        npc.loadFromAsset(cachedAsset);
      } catch {
        // Not cached, load directly
        await npc.load();
      }
    } else {
      await npc.load();
    }
    
    // Apply health overrides from spawn data (map authoring)
    if (data.maxHealth !== undefined) {
      npc.setHealth(data.maxHealth, data.startHealth);
    } else if (data.startHealth !== undefined) {
      // Only startHealth specified - use character config max with custom start
      const charMaxHealth = charConfig.health?.max ?? 100;
      npc.setHealth(charMaxHealth, data.startHealth);
    }
    
    // Set initial position
    npc.playerT = data.spawnT;
    const splinePoint = this.splineConstraint.getPointAt(data.spawnT);
    logger.npc.info(`🎯 SpawnNPC: marker position=${data.position}, spawnT=${data.spawnT}, splinePoint=${splinePoint}`);
    npc.setPosition(splinePoint);
    npc.character.position.copyFrom(splinePoint);
    npc.character.position.y = splinePoint.y + 2.0; // Start above ground
    
    // Create AI behavior using new composable behavior system
    // Build behavior config from spawn data + map annotations
    const behaviorConfig: NPCBehaviorConfig = {
      ...data.behaviorConfig,
      spawnT: data.spawnT,
      splineLength: this.splineConstraint.getTotalLength(),
      patrolLeft: data.patrolLeftDistance,
      patrolRight: data.patrolRightDistance,
    };
    
    // Resolve behavior (uses map config or defaults to passive_patrol)
    const behavior = resolveBehavior(behaviorConfig);
    const detectionRange = behaviorConfig.detectionRange ?? 10.0;
    const behaviorAdapter = new BehaviorAdapter(behavior, undefined, detectionRange);
    
    // Track adapter for player distance updates
    instance.behaviorAdapter = behaviorAdapter;
    
    // Set as AI behavior
    npc.setAIBehavior(behaviorAdapter);
    
    // Wire damage callback to behavior adapter
    npc.setDamageCallback((context) => {
      const shouldApply = behaviorAdapter.onDamage(npc, context.baseDamage, context.attackerId ?? 'unknown');
      // If behavior cancels damage, return 0; otherwise return original damage
      return shouldApply ? context.baseDamage : 0;
    });
    
    // Call onSpawn hook
    behaviorAdapter.onSpawn(npc);
    
    logger.npc.info(`🧠 Created behavior: ${behavior.id} (${behavior.displayName}) for ${data.id}`);
    
    // Legacy: Also create SplinePatrolAI for backwards compatibility until fully migrated
    const patrolConfig: SplinePatrolConfig = {
      leftDistance: data.patrolLeftDistance,
      rightDistance: data.patrolRightDistance,
      waitTime: data.patrolWaitTime,
    };
    const patrolAI = new SplinePatrolAI(data.spawnT, patrolConfig, this.splineConstraint);
    
    // Register with EntityRegistry for State Panel visibility
    this.entityRegistry.registerEntityInfo({
      id: data.id,
      type: EntityType.CHARACTER,
      characterType: data.characterId,
      characterSubtype: CharacterSubtype.NEUTRAL, // World NPC
      displayName: `${charConfig.name || data.characterId} (NPC)`,
      mesh: npc.character,
      metadata: {
        spawnData: data,
        behavior,
        patrolAI, // Keep for debug panel compatibility
      },
    });
    
    // Reset physics to apply position
    npc.resetPhysics();
    
    // Track instance
    instance.controller = npc;
    instance.isSpawned = true;
    this.activeNPCs.push(npc);
    
    logger.npc.info(`✨ Spawned NPC: ${data.id} (${data.characterId}) at T=${data.spawnT.toFixed(3)}`);
    
    return npc;
  }
  
  /**
   * Despawn an NPC (remove from scene but keep spawn data)
   */
  despawnNPC(spawnId: string): void {
    const instance = this.spawns.get(spawnId);
    if (!instance || !instance.isSpawned || !instance.controller) {
      return;
    }
    
    // Remove from active NPCs
    const index = this.activeNPCs.indexOf(instance.controller);
    if (index !== -1) {
      this.activeNPCs.splice(index, 1);
    }
    
    // Dispose controller
    instance.controller.dispose();
    
    // Unregister from EntityRegistry
    this.entityRegistry.unregister(instance.spawnData.id);
    
    // Update instance state
    instance.controller = null;
    instance.isSpawned = false;
    
    logger.npc.info(`💨 Despawned NPC: ${spawnId}`);
  }
  
  /**
   * Mark an NPC as dead
   * Note: Actual despawning is deferred until death animation completes (handled in update loop)
   */
  markDead(spawnId: string): void {
    const instance = this.spawns.get(spawnId);
    if (instance) {
      instance.isDead = true;
      logger.npc.info(`☠️ NPC ${spawnId} marked as dead, awaiting death animation`);
      // Despawn is now handled in update() via consumeDespawnRequest()
      // This allows the death animation to play before the NPC is removed
    }
  }
  
  /**
   * Get all active NPC controllers
   */
  getActiveNPCs(): NPCController[] {
    return this.activeNPCs;
  }
  
  /**
   * Get an NPC by spawn ID
   */
  getNPC(spawnId: string): NPCController | null {
    return this.spawns.get(spawnId)?.controller ?? null;
  }
  
  /**
   * Get all registered spawn data
   */
  getAllSpawns(): NPCSpawnData[] {
    return Array.from(this.spawns.values()).map(i => i.spawnData);
  }
  
  /**
   * Clear all spawns and NPCs (call on map unload)
   */
  clear(): void {
    // Despawn all active NPCs
    for (const npc of [...this.activeNPCs]) {
      const id = npc.getId();
      this.despawnNPC(id);
    }
    
    this.spawns.clear();
    this.activeNPCs = [];
    
    logger.npc.info('NPCSpawnManager cleared');
  }
  
  /**
   * Disable auto-spawning (useful during loading)
   */
  setAutoSpawn(enabled: boolean): void {
    this.autoSpawnEnabled = enabled;
  }
}
